// src/persistence/localStorageAdapter.ts
var createLocalStorageAdapter = () => ({
  async setItem(key, value) {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(key, value);
    }
  },
  async getItem(key) {
    if (typeof window !== "undefined" && window.localStorage) {
      return window.localStorage.getItem(key);
    }
    return null;
  },
  async removeItem(key) {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.removeItem(key);
    }
  }
});
var localStorageAdapter = createLocalStorageAdapter();

// src/persistence/tauriAdapter.ts
var createTauriAdapter = () => {
  const getTauriApi = async () => {
    if (typeof window !== "undefined" && "__TAURI__" in window) {
      const { invoke } = window.__TAURI__.core;
      return { invoke };
    }
    return null;
  };
  return {
    async setItem(key, value) {
      const api = await getTauriApi();
      if (api) {
        await api.invoke("store_set_item", { key, value });
      }
    },
    async getItem(key) {
      const api = await getTauriApi();
      if (api) {
        return await api.invoke("store_get_item", { key });
      }
      return null;
    },
    async removeItem(key) {
      const api = await getTauriApi();
      if (api) {
        await api.invoke("store_remove_item", { key });
      }
    }
  };
};
var tauriAdapter = createTauriAdapter();

// src/scriptStore.ts
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
var splitIntoLines = (text) => {
  return text.split("\n");
};
var getLineFromPosition = (text, position) => {
  const textUpToPosition = text.substring(0, position);
  return textUpToPosition.split("\n").length - 1;
};
var createAsyncStorage = (adapter) => ({
  getItem: async (name) => {
    const value = await adapter.getItem(name);
    return value ?? null;
  },
  setItem: async (name, value) => {
    await adapter.setItem(name, value);
  },
  removeItem: async (name) => {
    await adapter.removeItem(name);
  }
});
var createScriptStore = (adapter = localStorageAdapter) => create()(
  persist(
    (set) => ({
      text: "",
      currentPosition: 0,
      currentLine: 0,
      confidence: 0,
      lines: [],
      setText: (text) => set({
        text,
        lines: splitIntoLines(text),
        currentPosition: 0,
        currentLine: 0,
        confidence: 0
      }),
      setCurrentPosition: (position) => set((state) => ({
        currentPosition: position,
        currentLine: getLineFromPosition(state.text, position)
      })),
      setCurrentLine: (line) => set({ currentLine: line }),
      setConfidence: (confidence) => set({ confidence }),
      reset: () => set({
        currentPosition: 0,
        currentLine: 0,
        confidence: 0
      })
    }),
    {
      name: "vsp-script-storage",
      storage: createJSONStorage(() => createAsyncStorage(adapter)),
      partialize: (state) => ({ text: state.text })
    }
  )
);
var useScriptStore = createScriptStore();

// src/settingsStore.ts
import { create as create2 } from "zustand";
import { persist as persist2, createJSONStorage as createJSONStorage2 } from "zustand/middleware";
var createAsyncStorage2 = (adapter) => ({
  getItem: async (name) => {
    const value = await adapter.getItem(name);
    return value ?? null;
  },
  setItem: async (name, value) => {
    await adapter.setItem(name, value);
  },
  removeItem: async (name) => {
    await adapter.removeItem(name);
  }
});
var getInitialTheme = () => {
  try {
    const stored = localStorage.getItem("vsp-settings-storage");
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.state?.theme) return parsed.state.theme;
    }
  } catch {
  }
  if (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: light)").matches) {
    return "light";
  }
  return "dark";
};
var createSettingsStore = (adapter = localStorageAdapter) => create2()(
  persist2(
    (set) => ({
      fontSize: 32,
      lineHeight: 1.8,
      theme: getInitialTheme(),
      viewMode: "edit",
      mirrorMode: false,
      autoScrollEnabled: true,
      scrollSpeed: 5,
      highlightColor: "#fbbf24",
      // amber-400
      setFontSize: (fontSize) => set({ fontSize: Math.max(16, Math.min(72, fontSize)) }),
      setLineHeight: (lineHeight) => set({ lineHeight: Math.max(1, Math.min(3, lineHeight)) }),
      setTheme: (theme) => set({ theme }),
      setViewMode: (viewMode) => set({ viewMode }),
      setMirrorMode: (mirrorMode) => set({ mirrorMode }),
      setAutoScrollEnabled: (autoScrollEnabled) => set({ autoScrollEnabled }),
      setScrollSpeed: (scrollSpeed) => set({ scrollSpeed: Math.max(1, Math.min(10, scrollSpeed)) }),
      setHighlightColor: (highlightColor) => set({ highlightColor }),
      toggleTheme: () => set((state) => ({ theme: state.theme === "dark" ? "light" : "dark" }))
    }),
    {
      name: "vsp-settings-storage",
      storage: createJSONStorage2(() => createAsyncStorage2(adapter))
    }
  )
);
var useSettingsStore = createSettingsStore();

// src/recognitionStore.ts
import { create as create3 } from "zustand";
var createRecognitionStore = () => create3()((set) => ({
  status: "idle",
  interimText: "",
  finalText: "",
  errorMessage: null,
  hasPermission: null,
  isSupported: typeof window !== "undefined" && ("SpeechRecognition" in window || "webkitSpeechRecognition" in window),
  setStatus: (status) => set({ status }),
  setInterimText: (interimText) => set({ interimText }),
  setFinalText: (finalText) => set({ finalText }),
  setErrorMessage: (errorMessage) => set({ errorMessage }),
  setHasPermission: (hasPermission) => set({ hasPermission }),
  setIsSupported: (isSupported) => set({ isSupported }),
  reset: () => set({
    status: "idle",
    interimText: "",
    finalText: "",
    errorMessage: null
  })
}));
var useRecognitionStore = createRecognitionStore();

// src/recordingStore.ts
import { create as create4 } from "zustand";
var initialState = {
  status: "idle",
  currentChunkIndex: 0,
  totalDuration: 0,
  currentChunkDuration: 0,
  chunks: [],
  transcriptions: [],
  errorMessage: null,
  completedChunks: 0,
  processingChunks: 0,
  mergedSegments: [],
  srtReady: false,
  mergedAudioBlob: null,
  audioReady: false
};
var createRecordingStore = () => create4()((set) => ({
  ...initialState,
  setStatus: (status) => set({ status }),
  setCurrentChunkIndex: (currentChunkIndex) => set({ currentChunkIndex }),
  setTotalDuration: (totalDuration) => set({ totalDuration }),
  setCurrentChunkDuration: (currentChunkDuration) => set({ currentChunkDuration }),
  addChunk: (chunk) => set((state) => ({
    chunks: [...state.chunks, chunk]
  })),
  addTranscription: (transcription) => set((state) => ({
    transcriptions: [...state.transcriptions, transcription]
  })),
  updateTranscription: (chunkIndex, update) => set((state) => ({
    transcriptions: state.transcriptions.map(
      (t) => t.chunkIndex === chunkIndex ? { ...t, ...update } : t
    )
  })),
  setErrorMessage: (errorMessage) => set({ errorMessage }),
  incrementCompletedChunks: () => set((state) => ({
    completedChunks: state.completedChunks + 1
  })),
  setProcessingChunks: (processingChunks) => set({ processingChunks }),
  setMergedSegments: (mergedSegments) => set({ mergedSegments }),
  setSrtReady: (srtReady) => set({ srtReady }),
  setMergedAudioBlob: (mergedAudioBlob) => set({ mergedAudioBlob }),
  setAudioReady: (audioReady) => set({ audioReady }),
  reset: () => set(initialState)
}));
var useRecordingStore = createRecordingStore();

// src/subtitleSettingsStore.ts
import { create as create5 } from "zustand";
import { persist as persist3, createJSONStorage as createJSONStorage3 } from "zustand/middleware";
var createAsyncStorage3 = (adapter) => ({
  getItem: async (name) => {
    const value = await adapter.getItem(name);
    return value ?? null;
  },
  setItem: async (name, value) => {
    await adapter.setItem(name, value);
  },
  removeItem: async (name) => {
    await adapter.removeItem(name);
  }
});
var createSubtitleSettingsStore = (adapter = localStorageAdapter) => create5()(
  persist3(
    (set) => ({
      maxCharsPerLine: 25,
      linesPerSubtitle: 1,
      openaiApiKey: "",
      useScriptReference: true,
      setMaxCharsPerLine: (maxCharsPerLine) => set({ maxCharsPerLine: Math.max(15, Math.min(40, maxCharsPerLine)) }),
      setLinesPerSubtitle: (linesPerSubtitle) => set({ linesPerSubtitle: Math.max(1, Math.min(2, linesPerSubtitle)) }),
      setOpenaiApiKey: (openaiApiKey) => set({ openaiApiKey }),
      setUseScriptReference: (useScriptReference) => set({ useScriptReference })
    }),
    {
      name: "vsp-subtitle-settings-storage",
      storage: createJSONStorage3(() => createAsyncStorage3(adapter))
    }
  )
);
var useSubtitleSettingsStore = createSubtitleSettingsStore();
export {
  createLocalStorageAdapter,
  createRecognitionStore,
  createRecordingStore,
  createScriptStore,
  createSettingsStore,
  createSubtitleSettingsStore,
  createTauriAdapter,
  localStorageAdapter,
  tauriAdapter,
  useRecognitionStore,
  useRecordingStore,
  useScriptStore,
  useSettingsStore,
  useSubtitleSettingsStore
};
//# sourceMappingURL=index.js.map